import React, { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import classes from "./results.module.css";

const CSV_URL =
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vQ7ereCPPlms3UtnyQkS_wxeUqVmRJTbv1hNiFcDrK8uyJPFvcDSCBvjnUdLA3an1B2fM63hsI9PrgK/pub?output=csv";

const PIE_COLORS = ["#0039C7", "#2F6FED", "#6F9FE8", "#A7BDE5", "#DCE4F2"];
const STACK_COLORS = {
    strong: "#0039C7",
    interest: "#2F6FED",
    useful: "#A7BDE5",
    other: "#E8EEF8",
};

// Canonical profile values matched against CSV data (DO NOT translate). Each maps to
// an i18n key used only for the chart display label.
const PROFILE_LABELS = [
    "Производственный инженер/Өндіріс инженері",
    "Молодой специалист/Жас маман",
    "Исследователь/Зерттеуші",
    "Иное",
];

const PROFILE_LABEL_KEYS = {
    "Производственный инженер/Өндіріс инженері": "results.profile.opt1",
    "Молодой специалист/Жас маман": "results.profile.opt2",
    "Исследователь/Зерттеуші": "results.profile.opt3",
    "Иное": "results.profile.other",
};

// Keys = canonical header prefixes matched against CSV data (DO NOT translate).
// Values = i18n keys for the short label displayed on charts.
const QUESTION_SHORT_LABELS = {
    "Интересно ли вам узнать, какие возможности даёт цифровизация бизнес-процессов во взаимодействии между потребителем и контролирующей организацией":
        "results.shortLabel.q1",
    "Интересно ли вам узнать о подходах цифровизации, которые помогают принимать обоснованные решения по количественной оценке водных ресурсов":
        "results.shortLabel.q2",
    "Интересно ли вам разобраться, какие современные методы измерения расхода воды позволяют получать более точные и надёжные данные":
        "results.shortLabel.q3",
    "Интересно ли вам узнать, как современные системы мониторинга и автоматического регулирования помогают эффективно управлять уровнем и расходом воды":
        "results.shortLabel.q4",
    "Интересно ли вам разобраться, как математические модели используются для анализа и прогнозирования состояния водных ресурсов":
        "results.shortLabel.q5",
    "Интересно ли вам разобраться, как можно заранее оценивать возможные изменения водообеспеченности региона":
        "results.shortLabel.q6",
    "Полезно ли вам разобраться, как сценарное моделирование помогает управлять водными ресурсами":
        "results.shortLabel.q7",
    "Интересно ли вам разобраться, как современные измерительные комплексы и автоматизированные механизмы регулирования помогают эффективно управлять подачей и распределением воды":
        "results.shortLabel.q8",
    "Интересно ли вам разобраться, как интеграция данных из разных источников помогает формировать целостную картину состояния водных ресурсов региона":
        "results.shortLabel.q9",
    "Интересно ли вам узнать, какие программные решения и алгоритмы помогают оценивать эффективность распределения воды":
        "results.shortLabel.q10",
};

function normalizeValue(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim();
}

function isMetaHeader(header) {
    const h = header.toLowerCase();
    return (
        h.includes("timestamp") ||
        h.includes("отметка времени") ||
        h.includes("время") ||
        h.includes("time") ||
        h === ""
    );
}

function buildChoiceData(values) {
    const map = {};
    values
        .map(normalizeValue)
        .filter(Boolean)
        .forEach((v) => {
            map[v] = (map[v] || 0) + 1;
        });
    return Object.entries(map)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
}

function shortenQuestion(text, t) {
    if (!text) return "";
    const found = Object.keys(QUESTION_SHORT_LABELS).find((key) =>
        text.startsWith(key)
    );
    if (found) {
        return t(QUESTION_SHORT_LABELS[found]);
    }
    return text.length > 60 ? text.slice(0, 60) + "..." : text;
}

function classifyResponse(value) {
    const v = normalizeValue(value).toLowerCase();
    if (!v) return "other";
    if (v.includes("хочу понять") || v.includes("түсінгім келеді")) {
        return "strong";
    }
    if (v.includes("интересно узнать") || v.includes("білгім келеді")) {
        return "interest";
    }
    if (v.includes("полезно понять") || v.includes("түсіну пайдалы")) {
        return "useful";
    }
    return "other";
}

function buildThematicQuestionStats(header, values, totalResponses, t) {
    const counts = { strong: 0, interest: 0, useful: 0, other: 0 };
    values.map(normalizeValue).filter(Boolean).forEach((value) => {
        const group = classifyResponse(value);
        counts[group] += 1;
    });
    const safeTotal = totalResponses || 1;
    return {
        fullTitle: header,
        shortTitle: shortenQuestion(header, t),
        strong: Number(((counts.strong / safeTotal) * 100).toFixed(1)),
        interest: Number(((counts.interest / safeTotal) * 100).toFixed(1)),
        useful: Number(((counts.useful / safeTotal) * 100).toFixed(1)),
        other: Number(((counts.other / safeTotal) * 100).toFixed(1)),
        strongCount: counts.strong,
        totalCount: counts.strong + counts.interest + counts.useful + counts.other,
    };
}

function buildProfileData(values, t) {
    const counts = {
        "Производственный инженер/Өндіріс инженері": 0,
        "Молодой специалист/Жас маман": 0,
        "Исследователь/Зерттеуші": 0,
        "Иное": 0,
    };

    values.forEach((raw) => {
        const value = normalizeValue(raw);
        if (!value) return;
        const normalized = value.replace(/\s*,\s*/g, ",");
        const parts = normalized
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        if (!parts.length) return;
        parts.forEach((part) => {
            if (part === "Производственный инженер/Өндіріс инженері") {
                counts["Производственный инженер/Өндіріс инженері"] += 1;
            } else if (part === "Молодой специалист/Жас маман") {
                counts["Молодой специалист/Жас маман"] += 1;
            } else if (part === "Исследователь/Зерттеуші") {
                counts["Исследователь/Зерттеуші"] += 1;
            } else {
                counts["Иное"] += 1;
            }
        });
    });

    return PROFILE_LABELS.map((label) => ({
        name: t(PROFILE_LABEL_KEYS[label]),
        value: counts[label],
    })).filter((item) => item.value > 0);
}

function renderPieLabel({ cx, cy, midAngle, outerRadius, value, index }) {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 22;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x}
            y={y}
            fill={PIE_COLORS[index % PIE_COLORS.length]}
            textAnchor={x > cx ? "start" : "end"}
            dominantBaseline="central"
            fontSize={16}
            fontWeight={500}
        >
            {value}
        </text>
    );
}

function PieQuestionCard({ title, data }) {
    if (!data?.length) return null;
    return (
        <div className={classes.chartCard}>
            <h3 className={classes.chartCardTitle}>{title}</h3>
            <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="56%"
                            outerRadius={98}
                            stroke="#ffffff"
                            strokeWidth={1.5}
                            labelLine={true}
                            label={renderPieLabel}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={`pie-cell-${index}`}
                                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className={classes.legend}>
                {data.map((item, index) => (
                    <div key={item.name} className={classes.legendItem}>
                        <span
                            className={classes.legendDot}
                            style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                        />
                        <span>{item.name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProfileBarCard({ title, data }) {
    if (!data?.length) return null;
    return (
        <div className={classes.chartCard}>
            <h3 className={classes.chartCardTitle}>{title}</h3>
            <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ top: 10, right: 20, left: 90, bottom: 10 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={220}
                            tick={{ fontSize: 13, fill: "#163269" }}
                        />
                        <Tooltip />
                        <Bar dataKey="value" fill="#0039C7" radius={[0, 10, 10, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function ThematicStackedCard({ data }) {
    const { t } = useTranslation();
    if (!data?.length) return null;
    return (
        <div className={classes.chartCard}>
            <h3 className={classes.chartCardTitle}>
                {t("results.thematicComparison.title")}
            </h3>
            <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height={Math.max(460, data.length * 58)}>
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ top: 10, right: 30, left: 120, bottom: 10 }}
                        barCategoryGap={14}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <YAxis
                            type="category"
                            dataKey="shortTitle"
                            width={220}
                            tick={{ fontSize: 12, fill: "#163269" }}
                        />
                        <Tooltip
                            formatter={(value) => `${value}%`}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.fullTitle || label
                            }
                        />
                        <Legend />
                        <Bar dataKey="strong" name={t("results.group.strong")} stackId="a" fill={STACK_COLORS.strong} />
                        <Bar dataKey="interest" name={t("results.group.interest")} stackId="a" fill={STACK_COLORS.interest} />
                        <Bar dataKey="useful" name={t("results.group.useful")} stackId="a" fill={STACK_COLORS.useful} />
                        <Bar dataKey="other" name={t("results.group.other")} stackId="a" fill={STACK_COLORS.other} radius={[0, 8, 8, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function TopThemesCard({ data }) {
    const { t } = useTranslation();
    if (!data?.length) return null;
    return (
        <div className={classes.chartCard}>
            <h3 className={classes.chartCardTitle}>{t("results.topThemes.title")}</h3>
            <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                            dataKey="shortTitle"
                            angle={0}
                            textAnchor="end"
                            interval={0}
                            height={90}
                            dx={100}
                            tick={{ fontSize: 10, fill: "#163269" }}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.fullTitle || label
                            }
                        />
                        <Bar dataKey="strongCount" fill="#0039C7" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

const ResultsPage = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Papa.parse(CSV_URL, {
            download: true,
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
                setRows(result.data || []);
                setHeaders(result.meta.fields || []);
                setLoading(false);
            },
            error: (error) => {
                console.error("CSV parse error:", error);
                setLoading(false);
            },
        });
    }, []);

    const analytics = useMemo(() => {
        if (!rows.length || !headers.length) return null;

        const usefulHeaders = headers.filter((h) => !isMetaHeader(h));
        const totalResponses = rows.length;

        const firstQuestionHeader = usefulHeaders[0];
        const secondQuestionHeader = usefulHeaders[1];

        const profileHeader = usefulHeaders.find((h) => {
            const lower = h.toLowerCase();
            return (
                lower.includes("профиль деятельности") ||
                lower.includes("жұмыс салаңыз қандай") ||
                lower.includes("профиль")
            );
        });

        const thematicHeaders = usefulHeaders.filter((h) => h !== profileHeader);

        const firstQuestionData = firstQuestionHeader
            ? buildChoiceData(rows.map((row) => row[firstQuestionHeader]))
            : [];

        const secondQuestionData = secondQuestionHeader
            ? buildChoiceData(rows.map((row) => row[secondQuestionHeader]))
            : [];

        const profileQuestionData = profileHeader
            ? buildProfileData(rows.map((row) => row[profileHeader]), t)
            : [];

        const thematicStats = thematicHeaders
            .map((header) =>
                buildThematicQuestionStats(
                    header,
                    rows.map((row) => row[header]),
                    totalResponses,
                    t
                )
            )
            .filter((item) => item.totalCount > 0);

        const topThemes = [...thematicStats]
            .sort((a, b) => b.strongCount - a.strongCount)
            .slice(0, 5);

        const mostPopularTheme = topThemes[0]?.fullTitle || "—";
        const profileCount = profileQuestionData.length;

        return {
            totalResponses,
            firstQuestionHeader,
            secondQuestionHeader,
            firstQuestionData,
            secondQuestionData,
            profileHeader,
            profileQuestionData,
            thematicStats,
            topThemes,
            mostPopularTheme,
            profileCount,
            questionCount: thematicHeaders.length,
        };
    }, [rows, headers, t]);

    return (
        <div>
            <Header />
            <div className={classes.wrapper}>
                <div className={classes.container}>
                    <div className={classes.card}>
                        <span className={classes.badge}>{t("results.badge")}</span>
                        <h1 className={classes.title}>{t("results.title")}</h1>
                        <p className={classes.subtitle}>
                            {t("results.subtitle")}
                        </p>

                        {loading && (
                            <div className={classes.loading}>{t("results.loading")}</div>
                        )}

                        {!loading && !analytics && (
                            <div className={classes.loading}>
                                {t("results.loadError")}
                            </div>
                        )}

                        {!loading && analytics && (
                            <>
                                <div className={classes.statGrid}>
                                    <div className={`${classes.statCard} ${classes.statCardAccent}`}>
                                        <span className={classes.statValue}>
                                            {analytics.totalResponses}
                                        </span>
                                        <span className={classes.statLabel}>
                                            {t("results.stat.respondents")}
                                        </span>
                                    </div>
                                    <div className={classes.statCard}>
                                        <span className={classes.statValue}>
                                            {analytics.profileCount}
                                        </span>
                                        <span className={classes.statLabel}>
                                            {t("results.stat.profileCategories")}
                                        </span>
                                    </div>
                                    <div className={classes.statCard}>
                                        <span className={classes.statValue}>
                                            {analytics.questionCount}
                                        </span>
                                        <span className={classes.statLabel}>
                                            {t("results.stat.thematicQuestions")}
                                        </span>
                                    </div>
                                    <div className={classes.statCard}>
                                        <span className={classes.statValueSmall}>{t("results.stat.topTheme")}</span>
                                        <span className={classes.statLabel}>
                                            {shortenQuestion(analytics.mostPopularTheme, t)}
                                        </span>
                                    </div>
                                </div>

                                <div className={classes.chartGrid}>
                                    <PieQuestionCard
                                        title={analytics.firstQuestionHeader}
                                        data={analytics.firstQuestionData}
                                    />
                                    <PieQuestionCard
                                        title={analytics.secondQuestionHeader}
                                        data={analytics.secondQuestionData}
                                    />
                                </div>

                                <div className={classes.chartGridFull}>
                                    <ProfileBarCard
                                        title={
                                            analytics.profileHeader ||
                                            t("results.profileChartTitle")
                                        }
                                        data={analytics.profileQuestionData}
                                    />
                                </div>

                                <div className={classes.chartGridFull}>
                                    <ThematicStackedCard data={analytics.thematicStats} />
                                </div>

                                <div className={classes.chartGridFull}>
                                    <TopThemesCard data={analytics.topThemes} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ResultsPage;
